import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr, enGB } from "date-fns/locale";
import { Calendar } from "../ui/calendar";
import { useLang } from "../../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Booking = () => {
  const { t, lang } = useLang();
  const [services, setServices] = useState([]);
  const [acompte, setAcompte] = useState(50);
  const [timeSlots, setTimeSlots] = useState([]);
  const [serviceId, setServiceId] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [payOption, setPayOption] = useState("acompte");
  const [contact, setContact] = useState({ nom: "", email: "", telephone: "" });
  const [loading, setLoading] = useState(false);

  const dateLocale = lang === "fr" ? fr : enGB;

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => {
      setServices(r.data.services);
      setAcompte(r.data.acompte);
      setTimeSlots(r.data.time_slots);
    });
  }, []);

  const dateStr = date ? format(date, "yyyy-MM-dd") : null;

  const loadAvailability = useCallback((d) => {
    axios.get(`${API}/bookings/availability`, { params: { date: d } }).then((r) => setBookedSlots(r.data.booked_slots));
  }, []);

  useEffect(() => {
    if (dateStr) {
      setSlot(null);
      loadAvailability(dateStr);
    }
  }, [dateStr, loadAvailability]);

  const selectedService = services.find((s) => s.id === serviceId);
  const amount = payOption === "acompte" ? acompte : selectedService?.price;
  const ready = serviceId && date && slot && contact.nom && contact.email;

  const serviceLabel = (s) => t.booking.services[s.id] || s.label;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        ...contact,
        telephone: contact.telephone || null,
        service_id: serviceId,
        booking_date: dateStr,
        time_slot: slot,
        payment_option: payOption,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(t.booking.slotTaken);
        loadAvailability(dateStr);
      } else {
        toast.error(t.booking.error);
      }
      setLoading(false);
    }
  };

  return (
    <section id="reserver" className="relative bg-[#0A192F] py-24 md:py-32 overflow-hidden" data-testid="booking-section">
      <div className="noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-16"
        >
          <p className="font-script text-[#C9A03C] text-2xl mb-4">{t.booking.eyebrow}</p>
          <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl text-[#F0F0F0] font-medium leading-tight">
            {t.booking.title}
          </h2>
          <p className="mt-6 text-[#F0F0F0]/60 font-light leading-relaxed">{t.booking.intro(acompte)}</p>
        </motion.div>

        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-4">
            <p className="text-[#C9A03C] text-xs tracking-[0.2em] uppercase mb-2">{t.booking.step1}</p>
            {services.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => setServiceId(s.id)}
                data-testid={`booking-service-${s.id}`}
                className={`w-full text-left p-5 border transition-colors duration-300 ${
                  serviceId === s.id
                    ? "border-[#C9A03C] bg-[#C9A03C]/10"
                    : "border-white/10 hover:border-[#C9A03C]/50"
                }`}
              >
                <span className="block font-serif-lux text-lg text-[#F0F0F0]">{serviceLabel(s)}</span>
                <span className="block text-sm text-[#C9A03C] mt-1">{s.price.toFixed(0)} €</span>
              </button>
            ))}
          </div>

          <div className="lg:col-span-4">
            <p className="text-[#C9A03C] text-xs tracking-[0.2em] uppercase mb-6">{t.booking.step2}</p>
            <div className="bg-white/[0.04] border border-white/10 p-4 inline-block" data-testid="booking-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                locale={dateLocale}
                disabled={{ before: new Date() }}
                className="text-[#F0F0F0] [&_.rdp-day]:text-[#F0F0F0] [&_button]:text-[#F0F0F0]"
              />
            </div>
            {date && (
              <div className="mt-6">
                <p className="text-[#F0F0F0]/60 text-sm font-light mb-3">
                  {format(date, "EEEE d MMMM yyyy", { locale: dateLocale })}
                </p>
                <div className="flex flex-wrap gap-2">
                  {timeSlots.map((ts) => {
                    const taken = bookedSlots.includes(ts);
                    return (
                      <button
                        type="button"
                        key={ts}
                        disabled={taken}
                        onClick={() => setSlot(ts)}
                        data-testid={`booking-slot-${ts.replace(":", "")}`}
                        className={`px-4 py-2 text-sm border transition-colors duration-300 ${
                          taken
                            ? "border-white/5 text-white/20 line-through cursor-not-allowed"
                            : slot === ts
                            ? "border-[#C9A03C] bg-[#C9A03C] text-[#050B14]"
                            : "border-white/15 text-[#F0F0F0]/80 hover:border-[#C9A03C]"
                        }`}
                      >
                        {ts}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <p className="text-[#C9A03C] text-xs tracking-[0.2em] uppercase mb-6">{t.booking.step3}</p>
            <div className="space-y-5">
              <input
                required
                className="lux-input !text-[#F0F0F0] !border-white/20 focus:!border-[#C9A03C] placeholder:!text-white/30"
                style={{ color: "#F0F0F0" }}
                placeholder={t.booking.nom}
                value={contact.nom}
                onChange={(e) => setContact({ ...contact, nom: e.target.value })}
                data-testid="booking-input-nom"
              />
              <input
                required
                type="email"
                className="lux-input !text-[#F0F0F0] !border-white/20 focus:!border-[#C9A03C] placeholder:!text-white/30"
                style={{ color: "#F0F0F0" }}
                placeholder={t.booking.email}
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                data-testid="booking-input-email"
              />
              <input
                className="lux-input !text-[#F0F0F0] !border-white/20 focus:!border-[#C9A03C] placeholder:!text-white/30"
                style={{ color: "#F0F0F0" }}
                placeholder={t.booking.tel}
                value={contact.telephone}
                onChange={(e) => setContact({ ...contact, telephone: e.target.value })}
                data-testid="booking-input-telephone"
              />

              <div className="pt-4 space-y-3">
                <button
                  type="button"
                  onClick={() => setPayOption("acompte")}
                  data-testid="booking-pay-acompte"
                  className={`w-full text-left px-5 py-4 border transition-colors duration-300 ${
                    payOption === "acompte" ? "border-[#C9A03C] bg-[#C9A03C]/10" : "border-white/10 hover:border-[#C9A03C]/50"
                  }`}
                >
                  <span className="block text-sm text-[#F0F0F0]">{t.booking.acompteLabel}</span>
                  <span className="block text-[#C9A03C] font-serif-lux text-xl mt-1">{acompte.toFixed(0)} €</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPayOption("total")}
                  data-testid="booking-pay-total"
                  className={`w-full text-left px-5 py-4 border transition-colors duration-300 ${
                    payOption === "total" ? "border-[#C9A03C] bg-[#C9A03C]/10" : "border-white/10 hover:border-[#C9A03C]/50"
                  }`}
                >
                  <span className="block text-sm text-[#F0F0F0]">{t.booking.totalLabel}</span>
                  <span className="block text-[#C9A03C] font-serif-lux text-xl mt-1">
                    {selectedService ? `${selectedService.price.toFixed(0)} €` : t.booking.selectService}
                  </span>
                </button>
              </div>

              <button
                type="submit"
                disabled={!ready || loading}
                data-testid="booking-submit-button"
                className="w-full mt-4 px-10 py-4 bg-[#C9A03C] text-[#050B14] text-[13px] tracking-[0.16em] uppercase font-medium hover:bg-[#F0F0F0] transition-colors duration-400 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? t.booking.redirect : amount ? t.booking.payBtn(amount.toFixed(0)) : t.booking.payBtnEmpty}
              </button>
              <p className="text-xs text-white/30 font-light text-center">{t.booking.secured}</p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};
